import ModalComponent, { useModal } from "@/components/Modal";
import { useComponent } from "@/hooks/useMicrositeComponent";
import { cn } from "@/lib/utils";
import {
  IMicrositeComponent,
  MComponentImageSliderProps,
} from "@/types/microsite";
import assetUrl from "@/vars/assetUrl";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from "lucide-react";
import { Fragment, useEffect, useState } from "react";

import Button from "@/components/Button";
import { SpoilerButton } from "./Spoiler";

const ImageComponent = {
  kind: "IMAGE_SLIDE",
  Render: (props) => {
    const imageModal = useModal();
    const { focusTo } = useComponent();
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

    const [selected, setSelected] = useState<any>("");
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const {
      isBuilder,
      microsite,
      id,
      data: {
        isSpoiler,
        spoilerButtonColor,
        spoilerButtonTextPosition,
        spoilerButtonText,
        spoilerButtonIcon,
        spoilerButtonFont,
        needPassword,
        passwordMessage,
        style: _style,
        images: _images,
      },
    } = props;
    const [{ images }, setData] = useState({
      images: _images,
    });

    const imageStyle = _style || "default";

    const onProtectedOpen = (data) => {
      setData({ images: data.images });
    };

    useEffect(() => {
      setData({ images: _images });
    }, [props.data]);

    useEffect(() => {
      if (!emblaApi) return;

      const onSelect = () => {
        setCurrentIndex(emblaApi.selectedScrollSnap());
      };

      emblaApi.on("select", onSelect);
      onSelect();

      return () => {
        emblaApi.off("select", onSelect);
      };
    }, [emblaApi]);

    const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
    const scrollNext = () => emblaApi && emblaApi.scrollNext();
    const scrollTo = (index: number) => emblaApi && emblaApi.scrollTo(index);

    if ((!isSpoiler || !needPassword) && images.length === 0) {
      return <Fragment />;
    }

    return (
      <SpoilerButton
        needPassword={needPassword}
        spoilerButtonFont={spoilerButtonFont}
        microsite={microsite}
        spoilerButtonColor={spoilerButtonColor}
        spoilerButtonIcon={spoilerButtonIcon}
        spoilerButtonText={spoilerButtonText}
        spoilerButtonTextPosition={spoilerButtonTextPosition}
        isOpen={isOpen}
        isSpoiler={isSpoiler}
        onToggle={() => setIsOpen(!isOpen)}
        isBuilder={isBuilder}
        id={id}
        passwordMessage={passwordMessage}
        onProtectedOpen={onProtectedOpen}
        isFocus={props.isFocus}
      >
        <div className="relative p-2 w-full" id={`mcomp-${props.id}`}>
          {isBuilder && (
            <div
              className="top-0 right-0 bottom-0 left-0 z-10 absolute"
              onClick={() => {
                focusTo(id as string);
              }}
            />
          )}

          <div
            className={cn(
              "absolute top-0 bottom-0 right-3 left-3 flex justify-between items-center z-10 pointer-events-none",
              images.length > 1 ? "" : "opacity-0"
            )}
          >
            <div>
              <button
                onClick={scrollPrev}
                className="flex justify-center items-center bg-white shadow-md rounded-full size-7 pointer-events-auto"
              >
                <ChevronLeftIcon className="size-5 text-gray-800" />
              </button>
            </div>
            <div>
              <button
                onClick={scrollNext}
                className="flex justify-center items-center bg-white shadow-md rounded-full size-7 pointer-events-auto"
              >
                <ChevronRightIcon className="size-5 text-gray-800" />
              </button>
            </div>
          </div>
          <div
            className="bg-slate-200 rounded-md overflow-hidden embla"
            ref={emblaRef}
          >
            <div className="flex embla__container">
              {images.map(({ image }, index) => {
                return (
                  <div
                    key={index}
                    className="flex-[0_0_100%] min-w-0 embla__slide"
                  >
                    <div
                      className={cn(
                        "bg-white bg-no-repeat bg-center pb-[100%] w-full cursor-pointer",
                        imageStyle === "default" && "bg-cover",
                        imageStyle === "contain" && "bg-contain"
                      )}
                      style={{
                        backgroundImage: `url(${assetUrl + image})`,
                      }}
                      onClick={() => {
                        setSelected(image);
                        imageModal.open();
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          {images.length > 1 && (
            <div className="right-0 bottom-3 left-0 z-10 absolute flex justify-center items-center gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={cn(
                    "rounded-full w-2 h-2 cursor-pointer shadow-xl",
                    index === currentIndex ? "bg-white shadow" : "bg-white/50"
                  )}
                  onClick={() => scrollTo(index)}
                />
              ))}
            </div>
          )}
        </div>
        <ModalComponent {...imageModal} className="bg-white !max-w-[700px]">
          <Button
            variant="secondary"
            onClick={() => imageModal.close()}
            roundFull
            className="top-2 right-2 absolute size-10"
          >
            <XIcon className="size-6 text-gray-800" />
          </Button>
          <div className="-m-3 rounded-md overflow-hidden">
            <img
              src={assetUrl + selected}
              alt="image slide"
              className="w-full"
            />
          </div>
        </ModalComponent>
      </SpoilerButton>
    );
  },
} as IMicrositeComponent<MComponentImageSliderProps>;

export default ImageComponent;
